package com.schoolconnect.app.feature.schoolusers

import com.schoolconnect.app.datasource.network.response.PlatformUserResponse
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.request.SchoolStaffCreateRequest
import com.schoolconnect.app.datasource.network.request.SchoolStaffUpdateRequest
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.feature.common.DebouncedActionRunner
import com.schoolconnect.app.feature.common.AdminBootstrapSequencer
import com.schoolconnect.app.feature.common.AdminPagedListCoordinator
import com.schoolconnect.app.feature.common.ServerPagingState
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.feature.common.offset
import com.schoolconnect.app.feature.common.resolveServerPageTotal
import com.schoolconnect.app.feature.common.toWindow
import com.schoolconnect.app.feature.common.withPageIndex
import com.schoolconnect.app.feature.common.withPageSize
import com.schoolconnect.app.feature.common.withTotals
import com.schoolconnect.app.feature.schoolusers.add.AddSchoolUserFormValues
import com.schoolconnect.app.feature.schoolusers.add.addSchoolUserSchools
import com.schoolconnect.app.feature.schoolusers.edit.EditSchoolUserFormValues
import com.schoolconnect.app.feature.schoolusers.edit.validateEditSchoolUser
import com.schoolconnect.app.repository.schoolusers.SchoolUsersRepository
import com.schoolconnect.app.repository.schoolstaff.SchoolStaffRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import com.schoolconnect.app.repository.user.UserRepository
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.designsystem.AppToastCenter
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock

class SchoolUsersViewModel {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] SchoolUsersViewModel"
        val ROLE_IDS = listOf(4, 5)
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private var analyticsJob: Job? = null
    private val usersReloadDebouncer = DebouncedActionRunner(scope)
    private val bootstrapSequencer = AdminBootstrapSequencer(scope, TAG)
    private val pagedListCoordinator = AdminPagedListCoordinator(usersReloadDebouncer, bootstrapSequencer)

    private val _uiState = MutableStateFlow(
        SchoolUsersUiState(
            isLoading = true,
            paging = ServerPagingState()
        )
    )

    val uiState: StateFlow<SchoolUsersUiState> = _uiState.asStateFlow()

    init {
        refreshSchoolUsersData(forceRemote = false)
    }

    fun onEvent(event: SchoolUsersUiEvent) {
        when (event) {
            SchoolUsersUiEvent.BackClicked -> Unit
            SchoolUsersUiEvent.RefreshRequested -> {
                refreshSchoolUsersData(forceRemote = true)
            }
            is SchoolUsersUiEvent.SearchChanged -> updateFilters(
                searchQuery = event.query,
                resetPage = true
            )
            is SchoolUsersUiEvent.StatusFilterChanged -> updateFilters(
                statusFilter = event.statusFilter,
                resetPage = true
            )
            is SchoolUsersUiEvent.SchoolFilterChanged -> updateFilters(
                schoolFilterId = event.schoolId,
                schoolFilterName = event.schoolName,
                resetPage = true,
                refreshAnalytics = true
            )
            is SchoolUsersUiEvent.RoleFilterChanged -> updateFilters(
                roleFilter = event.role,
                resetPage = true
            )
            is SchoolUsersUiEvent.CreatedFromChanged -> updateFilters(
                createdFrom = event.createdFrom,
                resetPage = true
            )
            is SchoolUsersUiEvent.CreatedToChanged -> updateFilters(
                createdTo = event.createdTo,
                resetPage = true
            )
            SchoolUsersUiEvent.FilterModalOpened -> _uiState.update {
                it.copy(isFilterModalOpen = true, errorMessage = "")
            }
            SchoolUsersUiEvent.FilterModalClosed -> _uiState.update {
                it.copy(isFilterModalOpen = false)
            }
            SchoolUsersUiEvent.ToolbarReset,
            SchoolUsersUiEvent.FiltersReset -> {
                _uiState.update {
                    it.copy(
                        searchQuery = "",
                        statusFilter = SchoolUsersStatusFilter.ALL,
                        schoolFilterId = "",
                        schoolFilterName = "",
                        roleFilter = "",
                        createdFrom = "",
                        createdTo = "",
                        paging = it.paging.withPageIndex(0),
                        errorMessage = "",
                        infoMessage = "Filters cleared"
                    )
                }
                refreshSchoolUsersData(forceRemote = true)
            }
            is SchoolUsersUiEvent.SortChanged -> {
                _uiState.update {
                    val nextAscending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(
                        sortField = event.field,
                        sortAscending = nextAscending,
                        paging = it.paging.withPageIndex(0)
                    )
                }
                pagedListCoordinator.requestPage(forceRemote = true) { forceRemote ->
                    refreshSchoolUsers(forceRemote)
                }
            }
            is SchoolUsersUiEvent.PageChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageIndex(event.pageIndex)) }
                pagedListCoordinator.requestPage(forceRemote = false) { forceRemote ->
                    refreshSchoolUsers(forceRemote)
                }
            }
            is SchoolUsersUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageSize(event.pageSize)) }
                pagedListCoordinator.requestPage(forceRemote = false) { forceRemote ->
                    refreshSchoolUsers(forceRemote)
                }
            }
            is SchoolUsersUiEvent.ToggleStatus -> toggleStatus(event.userId)
            is SchoolUsersUiEvent.DeleteUser -> deleteUser(event.userId)
            is SchoolUsersUiEvent.ViewDetails -> openViewUserModal(event.userId)
            SchoolUsersUiEvent.ViewUserModalClosed -> closeViewUserModal()
            is SchoolUsersUiEvent.EditUser -> openEditUser(event.userId)
            SchoolUsersUiEvent.EditUserModalClosed -> closeEditUserModal()
            is SchoolUsersUiEvent.EditUserSubmitted -> saveEditedUser(event.userId, event.values)
            SchoolUsersUiEvent.AddUserClicked -> _uiState.update {
                it.copy(
                    isAddUserModalOpen = true,
                    isAddUserSaving = false,
                    errorMessage = "",
                    infoMessage = ""
                )
            }
            SchoolUsersUiEvent.AddUserModalClosed -> _uiState.update {
                it.copy(isAddUserModalOpen = false, isAddUserSaving = false)
            }
            is SchoolUsersUiEvent.AddUserSubmitted -> saveNewUser(event.values)
            SchoolUsersUiEvent.ClearMessages -> _uiState.update {
                it.copy(errorMessage = "", infoMessage = "")
            }
        }
    }

    private fun updateFilters(
        searchQuery: String = _uiState.value.searchQuery,
        statusFilter: SchoolUsersStatusFilter = _uiState.value.statusFilter,
        schoolFilterId: String = _uiState.value.schoolFilterId,
        schoolFilterName: String = _uiState.value.schoolFilterName,
        roleFilter: String = _uiState.value.roleFilter,
        createdFrom: String = _uiState.value.createdFrom,
        createdTo: String = _uiState.value.createdTo,
        resetPage: Boolean,
        refreshAnalytics: Boolean = false
    ) {
        _uiState.update {
            it.copy(
                searchQuery = searchQuery,
                statusFilter = statusFilter,
                schoolFilterId = schoolFilterId,
                schoolFilterName = schoolFilterName,
                roleFilter = roleFilter,
                createdFrom = createdFrom,
                createdTo = createdTo,
                paging = if (resetPage) it.paging.withPageIndex(0) else it.paging,
                errorMessage = "",
                infoMessage = ""
            )
        }
        if (refreshAnalytics) refreshSchoolUsersAnalytics(forceRemote = true)
        pagedListCoordinator.schedulePage { forceRemote -> refreshSchoolUsers(forceRemote) }
    }

    private fun refreshSchoolUsersData(forceRemote: Boolean) {
        pagedListCoordinator.refresh(
            forceRemote = forceRemote,
            loadAnalytics = { refreshSchoolUsersAnalytics(forceRemote) },
            loadPage = { pageForceRemote -> refreshSchoolUsers(pageForceRemote) }
        )
    }

    private fun refreshSchoolUsers(forceRemote: Boolean) {
        loadJob?.cancel()
        val currentState = _uiState.value
        val paging = currentState.paging

        loadJob = scope.launch {
            val normalizedSchoolId = currentState.schoolFilterId.trim().takeIf { it.isNotBlank() }
            val query = currentState.searchQuery.trim()
            val roleIds = resolveRoleIds(currentState.roleFilter)
            val requestedRoles = if (roleIds.isEmpty()) ROLE_IDS else roleIds
            val isActive = currentState.statusFilter.toIsActiveFilter()
            val createdFrom = currentState.createdFrom.toApiDateParam()
            val createdTo = currentState.createdTo.toApiDateParam()

            scLog(
                buildString {
                    append("$TAG refreshSchoolUsers start ")
                    append("q=${query.ifBlank { "<blank>" }} ")
                    append("school=${normalizedSchoolId ?: "<blank>"} ")
                    append("role=${currentState.roleFilter.ifBlank { "<all>" }} ")
                    append("status=${currentState.statusFilter} ")
                    append("pageIndex=${paging.pageIndex} ")
                    append("pageSize=${paging.pageSize} ")
                    append("forceRemote=$forceRemote")
                }
            )

            _uiState.update {
                it.copy(
                    isLoading = true,
                    errorMessage = "",
                    infoMessage = ""
                )
            }

            val result = SchoolUsersRepository.loadSchoolUsersPage(
                query = query,
                schoolId = normalizedSchoolId,
                roleIds = requestedRoles,
                isActive = isActive,
                createdFrom = createdFrom,
                createdTo = createdTo,
                pageIndex = paging.pageIndex,
                pageSize = paging.pageSize,
                sort = ServerSort(
                    field = currentState.sortField.apiField,
                    order = if (currentState.sortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
                ),
                forceRemote = forceRemote
            )

            result.fold(
                onSuccess = { response ->
                    val mappedUsers = response.users.orEmpty()
                        .distinctBy {
                            it.assignment?.assignmentId.orEmpty().ifBlank { it.id.orEmpty() }
                        }
                        .map { it.toSchoolUserRow() }
                    val sortedUsers = mappedUsers
                    val exactTotalCount = resolveServerPageTotal(
                        exactTotal = response.count,
                        paging = paging,
                        currentItemCount = sortedUsers.size,
                        pageOffset = paging.offset()
                    )
                    val nextPaging = paging.withTotals(
                        totalCount = exactTotalCount.totalCount,
                        isTotalCountExact = exactTotalCount.isExact
                    )
                    val pageWindow = nextPaging.toWindow(sortedUsers.size)
                    val clampedPageIndex = nextPaging.pageIndex.coerceAtMost(maxOf(0, pageWindow.pageCount - 1))

                    if (clampedPageIndex != nextPaging.pageIndex) {
                        _uiState.update { current ->
                            current.copy(
                                paging = current.paging.withPageIndex(clampedPageIndex),
                                isLoading = false
                            )
                        }
                        scope.launch {
                            refreshSchoolUsers(forceRemote = forceRemote)
                        }
                        return@fold
                    }

                    _uiState.update { current ->
                        current.copy(
                            isLoading = false,
                            users = sortedUsers,
                            paging = nextPaging,
                            errorMessage = "",
                            infoMessage = current.infoMessage
                        )
                    }
                    scLog(
                        "$TAG refreshSchoolUsers success count=${sortedUsers.size} totalCount=${nextPaging.totalCount} exact=${nextPaging.isTotalCountExact}"
                    )
                },
                onFailure = { throwable ->
                    if (throwable is CancellationException) {
                        scLog("$TAG refreshSchoolUsers cancelled")
                    } else {
                        scLog("$TAG refreshSchoolUsers failure", throwable)
                        _uiState.update {
                            it.copy(
                                isLoading = false,
                                errorMessage = throwable.message ?: "Failed to load school users"
                            )
                        }
                    }
                }
            )
        }
    }

    private fun refreshSchoolUsersAnalytics(forceRemote: Boolean) {
        analyticsJob?.cancel()
        val schoolId = _uiState.value.schoolFilterId.trim().takeIf { it.isNotBlank() }

        analyticsJob = scope.launch {
            scLog(
                "$TAG refreshSchoolUsersAnalytics start school=${schoolId ?: "<any>"} forceRemote=$forceRemote"
            )
            _uiState.update { it.copy(isAnalyticsLoading = true) }

            val result = SchoolUsersRepository.loadSchoolUsersAnalytics(
                schoolId = schoolId,
                forceRemote = forceRemote
            )

            result.fold(
                onSuccess = { analytics ->
                    _uiState.update {
                        it.copy(
                            isAnalyticsLoading = false,
                            analytics = analytics
                        )
                    }
                    scLog(
                        "$TAG refreshSchoolUsersAnalytics success total=${analytics.metrics.totalUsers} active=${analytics.metrics.activeUsers} inactive=${analytics.metrics.inactiveUsers} scope=${analytics.scope.ifBlank { "<blank>" }}"
                    )
                },
                onFailure = { throwable ->
                    if (throwable is CancellationException) {
                        scLog("$TAG refreshSchoolUsersAnalytics cancelled")
                    } else {
                        scLog("$TAG refreshSchoolUsersAnalytics failure", throwable)
                        _uiState.update {
                            it.copy(isAnalyticsLoading = false)
                        }
                    }
                }
            )
        }
    }

    private fun SchoolUsersStatusFilter.toIsActiveFilter(): Boolean? = when (this) {
        SchoolUsersStatusFilter.ALL -> null
        SchoolUsersStatusFilter.ACTIVE -> true
        SchoolUsersStatusFilter.INACTIVE -> false
    }

    private fun resolveRoleIds(roleFilter: String): List<Int> {
        val normalized = roleFilter.trim().lowercase()
        return when (normalized) {
            "", "all" -> ROLE_IDS
            "school admin" -> listOf(4)
            "school management", "school mgmt" -> listOf(5)
            else -> ROLE_IDS
        }
    }

    private fun PlatformUserResponse.toSchoolUserRow(): SchoolUserRowUi {
        val schoolLabel = assignment?.schoolName.orEmpty().ifBlank {
            assignment?.schoolCode.orEmpty().ifBlank { "Assigned school" }
        }
        val secondaryLabel = assignment?.schoolCode.orEmpty().ifBlank {
            createdAt.orEmpty().toCompactDateLabel()
        }
        return SchoolUserRowUi(
            id = id.orEmpty().ifBlank { "school-user-${Clock.System.now().toEpochMilliseconds()}" },
            name = displayUserName(),
            school = schoolLabel,
            schoolId = assignment?.schoolId.orEmpty(),
            assignmentId = assignment?.assignmentId.orEmpty(),
            staffId = assignment?.employeeId.orEmpty().ifBlank { assignment?.staffId.orEmpty() },
            staffRole = assignment?.roleTitle.orEmpty(),
            gender = assignment?.gender.orEmpty(),
            city = secondaryLabel,
            mobile = mobile.orEmpty(),
            email = email.orEmpty(),
            role = if (assignment?.roleId == 5) {
                SchoolUserRoleUi.SCHOOL_MANAGEMENT
            } else {
                SchoolUserRoleUi.SCHOOL_ADMIN
            },
            status = if (isActive == true) {
                SchoolUserStatusUi.ACTIVE
            } else {
                SchoolUserStatusUi.INACTIVE
            },
            createdOn = createdAt.orEmpty().toCompactDateLabel(),
            updatedOn = createdAt.orEmpty().toCompactDateLabel(),
            createdBy = "",
            updatedBy = ""
        )
    }

    private fun PlatformUserResponse.displayUserName(): String {
        val rawName = name.orEmpty()
        if (rawName.isNotBlank()) {
            return rawName.trim()
        }
        val emailLocal = email.orEmpty().substringBefore("@").trim()
        val normalizedEmail = emailLocal
            .replace('.', ' ')
            .replace('_', ' ')
            .replace('-', ' ')
            .trim()

        return when {
            normalizedEmail.isNotBlank() -> normalizedEmail.replaceFirstChar { it.uppercase() }
            mobile.orEmpty().isNotBlank() -> mobile.orEmpty()
            id.orEmpty().isNotBlank() -> id.orEmpty().take(8)
            else -> "School User"
        }
    }

    private fun String.toCompactDateLabel(): String {
        val trimmed = trim()
        if (trimmed.isBlank()) return ""

        val datePart = trimmed.takeIf { it.length >= 10 && it[4] == '-' && it[7] == '-' }?.substring(0, 10)
            ?: return trimmed.take(11)

        val parts = datePart.split("-")
        if (parts.size != 3) return trimmed.take(11)

        val year = parts[0]
        val month = parts[1].toIntOrNull() ?: return trimmed.take(11)
        val day = parts[2].toIntOrNull() ?: return trimmed.take(11)
        return "${day.toString().padStart(2, '0')} ${shortMonthLabel(month)} $year"
    }

    private fun String.toApiDateParam(): String {
        return appDateInputToApi(this).orEmpty()
    }

    private fun shortMonthLabel(month: Int): String = when (month) {
        1 -> "Jan"
        2 -> "Feb"
        3 -> "Mar"
        4 -> "Apr"
        5 -> "May"
        6 -> "Jun"
        7 -> "Jul"
        8 -> "Aug"
        9 -> "Sep"
        10 -> "Oct"
        11 -> "Nov"
        12 -> "Dec"
        else -> "Mon"
    }

    private fun saveNewUser(values: AddSchoolUserFormValues) {
        val schoolId = values.schoolId.trim()
        val schoolName = resolveSchoolName(schoolId)

        _uiState.update {
            it.copy(
                isLoading = true,
                isAddUserSaving = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            val result = SchoolStaffRepository.createSchoolStaff(
                SchoolStaffCreateRequest(
                    schoolId = schoolId,
                    employeeId = values.staffId.trim(),
                    name = values.fullName.trim(),
                    roleTitle = values.staffRole.trim().takeIf { it.isNotBlank() },
                    authRole = values.role.toStaffAuthRoleCode(),
                    gender = genderServerCode(values.gender),
                    isActive = true,
                    mobile = values.mobileNumber.trim(),
                    email = values.emailAddress.trim().takeIf { it.isNotBlank() }
                )
            )
            result.fold(
                onSuccess = {
                    refreshUsersAfterMutation(
                        successMessage = "Added ${values.fullName.trim()} to $schoolName",
                        resetPage = true,
                        closeAddModal = true
                    )
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isAddUserSaving = false,
                            errorMessage = throwable.message ?: "Failed to add school user"
                        )
                    }
                }
            )
        }
    }

    private fun resolveSchoolName(schoolId: String): String {
        val dynamicSchool = _uiState.value.schoolOptions.firstOrNull { it.id == schoolId }?.name.orEmpty()
        if (dynamicSchool.isNotBlank()) return dynamicSchool
        return addSchoolUserSchools.firstOrNull { it.id == schoolId }?.name ?: "Assigned school"
    }

    /** Convert the form's display label to the staff API's gender code. */
    private fun genderServerCode(value: String): String {
        val normalized = value.trim()
        if (normalized.isBlank()) return ""
        return SystemLookupOptions.code("GENDER", normalized).ifBlank {
            // The lookup endpoint documents M/F/O. This also covers a submit
            // that races the asynchronous global lookup warm-up.
            when (normalized.lowercase()) {
                "male" -> "M"
                "female" -> "F"
                "other" -> "O"
                else -> SystemLookupOptions.serverCode("GENDER", normalized)
            }
        }
    }

    private fun openEditUser(userId: String) {
        _uiState.update { state ->
            val user = state.users.firstOrNull { it.id == userId }
            state.copy(
                isEditUserModalOpen = user != null,
                editingUserId = user?.id.orEmpty(),
                isEditUserSaving = false,
                errorMessage = "",
                infoMessage = if (user == null) "User not found" else ""
            )
        }
    }

    private fun openViewUserModal(userId: String) {
        _uiState.update { state ->
            val user = state.users.firstOrNull { it.id == userId }
            state.copy(
                isViewUserModalOpen = user != null,
                viewingUserId = user?.id.orEmpty(),
                errorMessage = if (user == null) "User not found" else "",
                infoMessage = ""
            )
        }
    }

    private fun closeViewUserModal() {
        _uiState.update {
            it.copy(
                isViewUserModalOpen = false,
                viewingUserId = "",
                errorMessage = ""
            )
        }
    }

    private fun closeEditUserModal() {
        _uiState.update {
            it.copy(
                isEditUserModalOpen = false,
                editingUserId = "",
                isEditUserSaving = false
            )
        }
    }

    private fun saveEditedUser(userId: String, values: EditSchoolUserFormValues) {
        val validation = validateEditSchoolUser(values)
        if (validation.hasAnyError) {
            _uiState.update {
                it.copy(
                    errorMessage = "Please fix the highlighted fields before saving.",
                    infoMessage = ""
                )
            }
            return
        }
        val current = _uiState.value.users.firstOrNull { it.id == userId }
        if (current == null) {
            _uiState.update {
                it.copy(
                    isEditUserModalOpen = false,
                    editingUserId = "",
                    isEditUserSaving = false,
                    errorMessage = "User not found"
                )
            }
            return
        }

        _uiState.update {
            it.copy(
                isLoading = true,
                isEditUserSaving = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            val resolvedSchoolId = values.schoolId.ifBlank {
                addSchoolUserSchools.firstOrNull { it.name == values.schoolName }?.id.orEmpty()
            }
            val resolvedSchoolName = resolveSchoolName(resolvedSchoolId)
                .ifBlank { values.schoolName.trim().ifBlank { current.school } }
            val nextActive = values.status.equals("Active", ignoreCase = true)
            val staffId = SchoolStaffRepository.loadSchoolStaffs(resolvedSchoolId)
                .getOrNull()
                ?.firstOrNull { it.userId == userId }
                ?.id
                .orEmpty()
                .ifBlank { userId }

            val updateResult = SchoolStaffRepository.updateSchoolStaff(
                staffId = staffId,
                request = SchoolStaffUpdateRequest(
                    schoolId = resolvedSchoolId.takeIf { it.isNotBlank() } ?: current.schoolId,
                    userId = userId,
                    employeeId = values.staffId.trim().takeIf { it.isNotBlank() },
                    name = values.fullName.trim().takeIf { it.isNotBlank() },
                    roleTitle = values.staffRole.trim().takeIf { it.isNotBlank() },
                    authRole = values.role.toStaffAuthRoleCode(),
                    gender = values.gender.trim().takeIf { it.isNotBlank() }?.let(::genderServerCode),
                    isActive = nextActive,
                    mobile = values.mobileNumber.trim().takeIf { it.isNotBlank() },
                    email = values.emailAddress.trim().takeIf { it.isNotBlank() }
                ),
                currentUserId = UserSessionStore.getUserId().trim().takeIf { it.isNotBlank() }
            )
            if (updateResult.isFailure) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        isEditUserSaving = false,
                        errorMessage = updateResult.exceptionOrNull()?.message ?: "Failed to update school user"
                    )
                }
                return@launch
            }

            refreshUsersAfterMutation(
                successMessage = "Staff updated successfully",
                resetPage = false,
                closeEditModal = true
            )
        }
    }

    private fun toggleStatus(userId: String) {
        val current = _uiState.value.users.firstOrNull { it.id == userId }
        if (current == null) {
            _uiState.update { it.copy(errorMessage = "User not found") }
            return
        }

        val nextActive = current.status != SchoolUserStatusUi.ACTIVE
        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            UserRepository.toggleUserStatus(userId, nextActive).fold(
                onSuccess = {
                    refreshUsersAfterMutation(
                        successMessage = if (nextActive) {
                            "Activated ${current.name}"
                        } else {
                            "Inactivated ${current.name}"
                        },
                        resetPage = false
                    )
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Failed to update user status"
                        )
                    }
                }
            )
        }
    }

    private fun deleteUser(userId: String) {
        val current = _uiState.value.users.firstOrNull { it.id == userId }
        if (current == null) {
            _uiState.update { it.copy(errorMessage = "User not found") }
            return
        }

        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            UserRepository.deleteUser(userId).fold(
                onSuccess = {
                    refreshUsersAfterMutation(
                        successMessage = "Deleted ${current.name}",
                        resetPage = true
                    )
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Failed to delete user"
                        )
                    }
                }
            )
        }
    }

    private fun String.toStaffAuthRoleCode(): String = when (trim().lowercase()) {
        "school management", "management", "school mgmt" -> "SCHOOL_MGMT"
        else -> "SCHOOL_ADMIN"
    }

    private fun refreshUsersAfterMutation(
        successMessage: String,
        resetPage: Boolean,
        closeAddModal: Boolean = false,
        closeEditModal: Boolean = false
    ) {
        if (closeAddModal) {
            _uiState.update { it.copy(isAddUserModalOpen = false, isAddUserSaving = false) }
        }
        if (closeEditModal) {
            _uiState.update { it.copy(isEditUserModalOpen = false, editingUserId = "") }
        }
        if (resetPage) {
            _uiState.update { it.copy(paging = it.paging.withPageIndex(0)) }
        }

        _uiState.update { it.copy(infoMessage = "") }
        AppToastCenter.showSuccess(successMessage)
        SchoolUsersRepository.invalidateCaches()
        pagedListCoordinator.refresh(
            forceRemote = true,
            loadAnalytics = { refreshSchoolUsersAnalytics(forceRemote = true) },
            loadPage = { pageForceRemote -> refreshSchoolUsers(pageForceRemote) }
        )
        scope.launch {
            _uiState.update { state ->
                state.copy(
                    isEditUserSaving = false,
                    isAddUserSaving = false
                )
            }
        }
    }

    fun clear() {
        pagedListCoordinator.clear()
        loadJob?.cancel()
        analyticsJob?.cancel()
        scope.cancel()
    }
}
